import React, { useState } from 'react';

export default function CategoryPicker({ categories, value, subValue, onChange, onSubChange }) {
  const [step, setStep] = useState(value ? 2 : 1);
  const [showAll, setShowAll] = useState(false);

  if (!categories || categories.length === 0) {
    return (
      <div style={{ padding:'14px', background:'#FEF3C7', borderRadius:8, border:'1px solid #F59E0B', marginBottom:8 }}>
        <div style={{ fontSize:13, fontWeight:600, color:'#92400E' }}>⚠️ No categories yet</div>
        <div style={{ fontSize:12, color:'#B45309', marginTop:3 }}>Ask admin to add categories from the admin panel first.</div>
      </div>
    );
  }

  const selectedCat = categories.find(c => c.name.toLowerCase() === value);

  const handleCatSelect = (cat) => {
    onChange(cat.name.toLowerCase());
    onSubChange('');
    setStep(2);
    setShowAll(false);
  };

  const handleSubSelect = (sub) => onSubChange(sub.name);
  const handleChangeCat = () => { setStep(1); setShowAll(false); };

  const color = selectedCat?.color || '#1A6B3C';

  // Always use objects: {name, icon, image}
  const rawSubs = selectedCat?.subCategories || [];
  const subs = rawSubs
    .filter(s => s?.isActive !== false)
    .map(s => typeof s === 'string'
      ? { name: s, icon: '', image: '' }
      : { name: s.name || '', icon: s.icon || '', image: s.image || '' }
    );

  const visibleSubs = showAll ? subs : subs.slice(0, 12);

  return (
    <div style={{ marginBottom: 4 }}>

      {/* ── STEP 1: Category Grid ── */}
      {step === 1 && (
        <div>
          <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:8 }}>
            📂 Category *
          </label>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8 }}>
            {categories.map(cat => (
              <button
                key={cat._id}
                type="button"
                onClick={() => handleCatSelect(cat)}
                style={{
                  display:'flex', flexDirection:'column', alignItems:'center',
                  gap:5, padding:'10px 4px',
                  border:`1.5px solid ${cat.color || '#E5E7EB'}30`,
                  borderRadius:12, background:(cat.color||'#1A6B3C')+'0D',
                  cursor:'pointer', transition:'all .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background=(cat.color||'#1A6B3C')+'25'; e.currentTarget.style.borderColor=(cat.color||'#1A6B3C')+'80'; e.currentTarget.style.transform='scale(1.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.background=(cat.color||'#1A6B3C')+'0D'; e.currentTarget.style.borderColor=(cat.color||'#1A6B3C')+'30'; e.currentTarget.style.transform='scale(1)'; }}
              >
                {/* Image or icon */}
                <div style={{
                  width:46, height:46, borderRadius:'50%',
                  background: cat.color ? cat.color+'20' : '#E8F5EE',
                  border:`2px solid ${cat.color||'#1A6B3C'}40`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  overflow:'hidden', fontSize:22,
                }}>
                  {cat.image
                    ? <img src={cat.image} alt={cat.name} style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }} onError={e => { e.target.style.display='none'; }} />
                    : cat.icon ? <span>{cat.icon}</span> : null}
                </div>
                <span style={{
                  fontSize:10, fontWeight:600, color:cat.color||'#374151',
                  textAlign:'center', lineHeight:1.2, wordBreak:'break-word',
                }}>
                  {cat.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── STEP 2: Sub-category Grid ── */}
      {step === 2 && selectedCat && (
        <div>
          {/* Selected category pill */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{
                width:32, height:32, borderRadius:'50%',
                background:color+'20', border:`2px solid ${color}40`,
                display:'flex', alignItems:'center', justifyContent:'center',
                overflow:'hidden', fontSize:16,
              }}>
                {selectedCat.image
                  ? <img src={selectedCat.image} alt={selectedCat.name} style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }} onError={e => { e.target.style.display='none'; }} />
                  : selectedCat.icon ? <span>{selectedCat.icon}</span> : null}
              </div>
              <div>
                <span style={{ fontSize:13, fontWeight:700, color }}>{selectedCat.name}</span>
                {subValue && <span style={{ fontSize:11, color:'#6B7280', marginLeft:6 }}>→ {subValue}</span>}
              </div>
            </div>
            <button type="button" onClick={handleChangeCat}
              style={{ fontSize:11, color, fontWeight:600, background:color+'15', border:`1px solid ${color}40`, borderRadius:20, padding:'4px 10px', cursor:'pointer' }}>
              ✏️ Change
            </button>
          </div>

          <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:8 }}>
            🏷️ Sub-Category *
          </label>

          {subs.length === 0 && (
            <div style={{ padding:12, background:'#FEF3C7', borderRadius:8, border:'1px solid #F59E0B', fontSize:12, color:'#92400E' }}>
              ⚠️ No sub-categories added yet. Ask admin to add them.
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:6 }}>
            {visibleSubs.map(sub => {
              const isSelected = subValue === sub.name;
              return (
                <button
                  key={sub.name}
                  type="button"
                  onClick={() => handleSubSelect(sub)}
                  style={{
                    display:'flex', flexDirection:'column', alignItems:'center',
                    gap:4, padding:'8px 4px',
                    border:`2px solid ${isSelected ? color : color+'25'}`,
                    borderRadius:10,
                    background: isSelected ? color+'20' : color+'08',
                    cursor:'pointer', transition:'all .15s',
                    boxShadow: isSelected ? `0 0 0 2px ${color}30` : 'none',
                  }}
                >
                  {/* Sub image or emoji */}
                  <div style={{
                    width:42, height:42, borderRadius:'50%',
                    background: isSelected ? color+'30' : 'white',
                    border:`1.5px solid ${isSelected ? color : color+'30'}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    overflow:'hidden', fontSize:20,
                  }}>
                    {sub.image
                      ? <img src={sub.image} alt={sub.name} style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }} onError={e => { e.target.style.display='none'; }} />
                      : sub.icon ? <span>{sub.icon}</span> : null}
                  </div>
                  <span style={{
                    fontSize:10, fontWeight: isSelected ? 700 : 500,
                    color: isSelected ? color : '#4B5563',
                    textAlign:'center', lineHeight:1.2,
                  }}>
                    {sub.name}
                  </span>
                  {isSelected && (
                    <span style={{ fontSize:9, background:color, color:'white', borderRadius:4, padding:'1px 5px' }}>✓</span>
                  )}
                </button>
              );
            })}
          </div>

          {subs.length > 12 && (
            <button type="button" onClick={() => setShowAll(p => !p)}
              style={{ width:'100%', marginTop:8, padding:'7px', background:color+'10', border:`1px solid ${color}30`, borderRadius:8, fontSize:12, color, fontWeight:600, cursor:'pointer' }}>
              {showAll ? '▲ Show less' : `▼ Show all ${subs.length} varieties`}
            </button>
          )}

          {subValue && (
            <div style={{ marginTop:8, padding:'8px 12px', background:color+'15', borderRadius:8, border:`1px solid ${color}30`, display:'flex', alignItems:'center', gap:8 }}>
              <div>
                <span style={{ fontSize:12, fontWeight:700, color }}>✓ {selectedCat.name} → {subValue}</span>
                <div style={{ fontSize:11, color:'#6B7280' }}>Tap another to change</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function getSubEmoji(name) {
  const n = (typeof name === 'string' ? name : String(name||'')).toLowerCase();
  const map = {
    tomato:'🍅', carrot:'🥕', potato:'🥔', onion:'🧅', cabbage:'🥬',
    spinach:'🥬', chilli:'🌶️', brinjal:'🍆', beetroot:'🟣', peas:'🫛',
    beans:'🫘', cucumber:'🥒', pumpkin:'🎃', capsicum:'🫑', cauliflower:'🥦',
    garlic:'🧄', corn:'🌽', 'bitter gourd':'🥒', 'green peas':'🫛',
    mango:'🥭', banana:'🍌', guava:'🍈', papaya:'🍈', watermelon:'🍉',
    grapes:'🍇', orange:'🍊', apple:'🍎', lemon:'🍋', pineapple:'🍍',
    pomegranate:'🔴', jackfruit:'🟡', coconut:'🥥',
    rice:'🍚', wheat:'', maize:'🌽', jowar:'', bajra:'', ragi:'',
    barley:'', sorghum:'',
    turmeric:'🟡', pepper:'⚫', cardamom:'🟢', coriander:'',
    cumin:'🟤', fenugreek:'🟤', mustard:'🟡', cloves:'🟤', ginger:'🫚',
    marigold:'🌼', rose:'🌹', jasmine:'🌸', sunflower:'🌻',
    lotus:'🪷', hibiscus:'🌺', chrysanthemum:'🌸',
    tulsi:'', 'aloe vera':'🌵', neem:'', ashwagandha:'',
    brahmi:'', amla:'🟢', lemongrass:'',
    'toor dal':'🟡', 'moong dal':'🟡', 'chana dal':'🟡',
    'urad dal':'⚪', 'masoor dal':'🔴', chickpeas:'🟡', 'kidney beans':'🔴',
    mint:'', 'curry leaves':'🍃', moringa:'', amaranth:'',
    'fenugreek leaves':'', pluckk:'', other:'',
  };
  return map[n] || '';
}